/**
 * The one computation behind a KPI tile (SAD-998): its displayed value, its
 * target and its tone come from the same numbers, with the direction stated.
 * A tile never derives its colour from a different count, window or rounding
 * than the figure it prints.
 *
 * The bug this exists to end: CCSS's current period had 954 of 957 approvals
 * on time and 14 dates overdue. Math.round printed "100%", while the tone also
 * required "nothing overdue", so the tile read 100%, red, "Off target". Now
 * overdue dates are in the share itself: 954 of 971, 98%.
 */ /** On-time delivery's target (ryugu's KPI 1: every approval on time). Higher is better. */ export const ON_TIME_TARGET_PCT = 100;
/** CPARS / QASP overall-score floor, mirroring ryugu's kpiScoreThreshold. Higher is better. */ export const SCORE_TARGET = 4.0;
/**
 * A share as a whole percent that never rounds onto an edge it has not
 * reached: 954 of 957 is 99%, never 100%; 1 of 400 is 1%, never 0%. null
 * with nothing to divide by — a KPI must not print 0% for "nothing measured".
 */ export function wholePercent(part, whole) {
    if (whole <= 0) return null;
    const pct = Math.round(part / whole * 100);
    if (pct >= 100 && part < whole) return 99;
    if (pct <= 0 && part > 0) return 1;
    return pct;
}
/**
 * On-time delivery, the one definition every surface uses (owner, SAD-998):
 * approved on or before the due date, out of everything that has come due —
 * approvals with both dates known plus overdue dates not filed yet, which are
 * already misses. CCSS's Base Period: 954 / (957 + 14) = 98%.
 *
 * The tone compares the same counts with the target in exact integer
 * arithmetic, never the rounded figure, so the printed value and its colour
 * cannot disagree. No filing and nothing overdue is no data; overdue dates
 * with no filing are 0%, red.
 */ export function onTimeReading(counts) {
    const due = counts ? counts.timingKnown + counts.overdue : 0;
    if (!counts || due <= 0) return {
        pct: null,
        due: 0,
        status: 'nodata'
    };
    const meets = counts.onTime * 100 >= ON_TIME_TARGET_PCT * due;
    // due > 0, so the share is defined.
    const pct = wholePercent(counts.onTime, due);
    return {
        pct,
        due,
        status: meets ? 'green' : 'red'
    };
}
export function twoWayOnTime(c) {
    const missed = Math.min(c.untrackedLate, c.late);
    return {
        documents: onTimeReading({
            onTime: c.onTime,
            timingKnown: c.timingKnown,
            overdue: c.late
        }),
        tracker: onTimeReading({
            onTime: c.onTime,
            timingKnown: c.timingKnown,
            overdue: c.late - missed
        }),
        trackerMissed: missed,
        notCovered: c.notCovered,
        waived: c.waived,
        covered: c.covered ?? 0
    };
}
/**
 * A score to one decimal, truncated rather than rounded, so a score below the
 * floor never prints as the floor (3.95 reads 3.9, not 4.0).
 */ export function scoreText(score) {
    return (Math.floor(score * 10 + 1e-9) / 10).toFixed(1);
}
/**
 * CPARS / QASP score: each leg on record is compared with the floor on its
 * raw score; the tile is on target only when every leg on record is. A leg
 * not on record is absent, never a zero.
 */ export function scoreReading(s) {
    const legs = [];
    if (s.hasCpars) legs.push({
        name: 'CPARS',
        score: s.cpars,
        meets: s.cpars >= SCORE_TARGET
    });
    if (s.hasQasp) legs.push({
        name: 'QASP',
        score: s.qasp,
        meets: s.qasp >= SCORE_TARGET
    });
    if (legs.length === 0) return {
        legs,
        status: 'nodata',
        text: null
    };
    return {
        legs,
        status: legs.every((l)=>l.meets) ? 'green' : 'red',
        text: legs.map((l)=>`${l.name} ${scoreText(l.score)}`).join(' · ')
    };
}
/**
 * Funding consumed: lower is better, red once consumption reaches the alert
 * threshold (ryugu's ThresholdBreached rule, consumed >= threshold), computed
 * from the same figure the tile prints.
 */ export function fundingReading(f) {
    if (!f.hasData) return {
        pct: null,
        status: 'nodata'
    };
    return {
        pct: Math.floor(f.consumedPct + 1e-9),
        status: f.consumedPct >= f.thresholdPct ? 'red' : 'green'
    };
}
/** SDS audit checks passed: on target only when every scored check passed. */ export function auditReading(a) {
    if (!a.hasAudit || a.total <= 0) return {
        status: 'nodata'
    };
    return {
        status: a.pass === a.total ? 'green' : 'red'
    };
}
